import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Property, Tenant, RentPayment } from '../types/properties';
import { DollarSign, Plus, ArrowUpRight, ArrowDownRight, AlertTriangle } from 'lucide-react';
import { PaymentForm } from '../components/rent/PaymentForm';
import { RentReceipt } from '../components/rent/RentReceipt';
import { RentTracker } from '../components/properties/RentTracker';
import { Modal } from '../components/Modal';
import { formatCurrency } from '../lib/format';

export function RentDashboard() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [payments, setPayments] = useState<RentPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<RentPayment | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [
        { data: pData }, { data: tData }, { data: rData }
      ] = await Promise.all([
        supabase.from('properties').select('*'),
        supabase.from('tenants').select('*').eq('status', 'active'),
        supabase.from('rent_payments').select('*').order('payment_date', { ascending: false })
      ]);

      setProperties(pData || []);
      setTenants(tData || []);
      setPayments(rData || []);
    } catch (error) {
      console.error('Error fetching rent data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate stats based on current month
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const expectedRent = tenants.reduce((sum, t) => sum + (t.monthly_rent || 0), 0);

  const currentMonthPayments = payments.filter(p => {
    const d = new Date(p.payment_date);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear && p.status === 'paid';
  });

  const collectedRent = currentMonthPayments.reduce((sum, p) => sum + p.amount, 0);
  const outstandingRent = expectedRent - collectedRent;
  const collectionRate = expectedRent > 0 ? (collectedRent / expectedRent) * 100 : 0;

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <div className="flex items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-success-dim)] flex items-center justify-center">
            <DollarSign className="text-[var(--t-success)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)] m-0">Rent Collection</h1>
            <p className="text-sm text-[var(--t-text-muted)] mt-1">Track payments and manage cash flow</p>
          </div>
        </div>

        <button
          onClick={() => setShowPaymentModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
        >
          <Plus size={16} /> Record Payment
        </button>
      </div>

      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        <div className="max-w-6xl mx-auto space-y-8">

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-6 relative overflow-hidden">
              <div className="text-[var(--t-text-muted)] text-xs font-bold uppercase tracking-wider mb-2">Expected This Month</div>
              <div className="text-3xl font-black text-[var(--t-text)]">{formatCurrency(expectedRent)}</div>
            </div>
            <div className="bg-[var(--t-success-dim)] border border-[var(--t-success)]/20 rounded-2xl p-6 relative overflow-hidden">
              <div className="text-[var(--t-success)] text-xs font-bold uppercase tracking-wider mb-2">Collected</div>
              <div className="text-3xl font-black text-[var(--t-success)]">{formatCurrency(collectedRent)}</div>
              <div className="mt-2 text-sm font-medium text-[var(--t-success)]/80 flex items-center gap-1">
                <ArrowUpRight size={16} /> {collectionRate.toFixed(1)}% Collection Rate
              </div>
            </div>
            <div className={`bg-[var(--t-surface)] border rounded-2xl p-6 relative overflow-hidden ${outstandingRent > 0 ? 'border-[var(--t-warning)]/50' : 'border-[var(--t-border)]'}`}>
              <div className="text-[var(--t-text-muted)] text-xs font-bold uppercase tracking-wider mb-2">Outstanding</div>
              <div className={`text-3xl font-black ${outstandingRent > 0 ? 'text-[var(--t-warning)]' : 'text-[var(--t-text)]'}`}>
                {formatCurrency(Math.max(0, outstandingRent))}
              </div>
            </div>
            <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-6 relative overflow-hidden flex flex-col justify-center items-center text-center">
               <AlertTriangle className="text-[var(--t-error)] mb-2 opacity-80" size={24} />
               <div className="text-xs text-[var(--t-text-muted)] font-bold uppercase tracking-wider">0 Late Payments</div>
            </div>
          </div>

          {/* Payments Table */}
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-bold text-white">Payment Ledger</h2>
            <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-[var(--t-surface-hover)] border-b border-[var(--t-border)]">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Date</th>
                    <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Property</th>
                    <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Tenant</th>
                    <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Amount</th>
                    <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Status</th>
                    <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--t-border-subtle)]">
                  {payments.map(payment => {
                    const p = properties.find(prop => prop.id === payment.property_id);
                    const t = tenants.find(ten => ten.id === payment.tenant_id);
                    return (
                      <tr key={payment.id} className="hover:bg-[var(--t-surface-hover)]/50 transition-colors">
                        <td className="px-4 py-3 text-white">
                          {new Date(payment.payment_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-[var(--t-text)] truncate max-w-[200px]">
                          {p?.address || 'Unknown Property'}
                        </td>
                        <td className="px-4 py-3 text-[var(--t-text)] truncate max-w-[150px]">
                          {t?.name || 'Unknown Tenant'}
                        </td>
                        <td className="px-4 py-3 text-[var(--t-text)] font-medium">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`capitalize text-xs font-bold px-2 py-0.5 rounded ${
                            payment.status === 'paid' ? 'bg-[var(--t-success-dim)] text-[var(--t-success)]' :
                            payment.status === 'late' ? 'bg-[var(--t-error)]/10 text-[var(--t-error)]' :
                            'bg-[var(--t-warning-dim)] text-[var(--t-warning)]'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {payment.status === 'paid' && (
                            <button
                              onClick={() => setSelectedReceipt(payment)}
                              className="text-xs font-semibold text-[var(--t-primary)] hover:underline"
                            >
                              Receipt
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-[var(--t-text-muted)]">No payments recorded.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Record Payment" maxWidth="max-w-xl">
        <PaymentForm properties={properties} tenants={tenants} onClose={() => setShowPaymentModal(false)} onSuccess={() => { setShowPaymentModal(false); fetchData(); }} />
      </Modal>

      {selectedReceipt && (
        <Modal isOpen={!!selectedReceipt} onClose={() => setSelectedReceipt(null)} maxWidth="max-w-2xl" closeOnOutsideClick={false}>
          <RentReceipt
            payment={selectedReceipt}
            property={properties.find(p => p.id === selectedReceipt.property_id)!}
            tenant={tenants.find(t => t.id === selectedReceipt.tenant_id)}
            onClose={() => setSelectedReceipt(null)}
          />
        </Modal>
      )}
    </div>
  );
}
